import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Search, ShieldCheck, Wand2, GitCompareArrows, Send, Clock, X, Check } from "lucide-react";
import { MarketingNav, MarketingFooter } from "@/components/TailorWizMarketingHome";

const STEPS = [
  {
    num: "01",
    kicker: "Qualify",
    title: "Find Out If You Are Actually Qualified",
    icon: Search,
    time: "60 seconds",
    body: [
      "Paste the job description. TailorWiz instantly analyzes every requirement, qualification, skill, and expectation in the posting and cross-references it against your background line by line. In seconds it gives you a clear, honest answer.",
      "If you are qualified, TailorWiz tells you to go for it and moves you to the next step. If you are not qualified, it does not just tell you no. It tells you what you are missing, how significant the gaps are, and what roles you should be targeting instead based on where your background is actually strong.",
      "No more wasted applications. No more rejection emails for roles you never had a realistic shot at. No more passing on roles you were more than ready for because you talked yourself out of it.",
    ],
  },
  {
    num: "02",
    kicker: "Test",
    title: "Find Out If Your Resume Survives the Filter",
    icon: ShieldCheck,
    time: "60 seconds",
    body: [
      "TailorWiz runs your resume through two separate tests simultaneously. The 2026 ATS Algorithm Test checks whether your resume survives the automated filter that runs on 99% of employer systems. The Human Eye Test evaluates whether it passes the 7 to 10 second scan and holds up under detailed review.",
      "If you score 80% or higher on both, you are cleared to apply with your current document. If you score below 80%, TailorWiz moves you immediately into tailoring. Applying with a resume that fails these tests is not a strategy. It is a waste of a qualified application.",
    ],
  },
  {
    num: "03",
    kicker: "Tailor",
    title: "Generate the Full Document Package",
    icon: Wand2,
    time: "60 to 90 seconds",
    body: [
      "Select the documents you want on this run. Resume. Cover letter. Solution Asset. Outreach letter. Thank you letter. Executive bio. LinkedIn summary. Any combination, all in a single run.",
      "Choose from a menu of advanced tailoring methods or type your own custom instructions in plain language. Click Tailor My Resume. Wait 45 to 90 seconds.",
      "What comes back is not a keyword-stuffed AI-generated document. What comes back is a completely tailored, professionally written, fully positioned package that reads and performs as if it was personally written by a world renowned executive resume writer — because TailorWiz was trained to think and write exactly the way Marcus Hall does. 90% match or better. Every single time.",
    ],
  },
  {
    num: "04",
    kicker: "Review",
    title: "See Every Change. Approve Every Decision.",
    icon: GitCompareArrows,
    time: "2 minutes to review",
    body: [
      "Once your tailored documents are ready, TailorWiz does not just hand them to you and move on. It gives you a complete Before and After Report so you can see exactly what changed and why.",
      "The report shows your original match score, your new score, every gap that was closed, every keyword that was added, every responsibility that was repositioned, and a full explanation of every change made. Nothing in your document is a mystery.",
    ],
  },
  {
    num: "05",
    kicker: "Deliver",
    title: "Bypass the System. Land in the Inbox.",
    icon: Send,
    time: "5 to 10 minutes per direct contact",
    body: [
      "This is the step every other resume tool forgets. Submitting through the company's careers page is not the goal. Landing in the hiring manager's inbox is.",
      "TailorWiz finishes the job by helping you identify the actual decision maker for the role, sourcing their direct contact, and arming you with an Outreach Letter that bypasses the ATS, the recruiter, and the HR gatekeeper to land your documents in front of the only person with the power to call you in.",
      "Application black hole? Solved. 2% response rate? Solved. The hiring manager who would have hired you on the spot but never saw your resume? They see it now.",
    ],
  },
];

export default function HowItWorksPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <MarketingNav />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#0B1C3F] via-[#0E2350] to-[#0B1C3F] text-white">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.16]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 18% 25%, #C9A84C 0, transparent 38%), radial-gradient(circle at 82% 75%, #4A78C2 0, transparent 38%)",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 pt-16 pb-16 sm:pt-20 sm:pb-20 text-center">
          <span className="inline-flex items-center gap-2 text-[11px] sm:text-[12px] font-bold uppercase tracking-[0.18em] text-[#C9A84C] bg-white/5 px-4 py-2 rounded-full border border-[#C9A84C]/40">
            <Sparkles className="w-3.5 h-3.5 text-[#C9A84C]" />
            The Workflow
          </span>
          <h1 className="mt-6 text-[34px] sm:text-[48px] lg:text-[54px] font-extrabold tracking-tight leading-[1.07]">
            From Job Description to the
            <br className="hidden sm:inline" />{" "}
            <span className="text-[#C9A84C]">Hiring Manager's Inbox</span> — in 5 Steps
          </h1>
          <p className="mt-6 text-[16px] sm:text-[19px] leading-[1.6] text-slate-200">
            Every step inside TailorWiz was designed to solve one specific
            failure point in the modern job search.{" "}
            <span className="font-semibold text-white">
              Together they compress a four-to-six-month executive search into a
              thirty-day campaign.
            </span>
          </p>
        </div>
      </section>

      {/* STEPS */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <ol className="relative space-y-12">
            {/* vertical connector line */}
            <div className="absolute left-[31px] top-4 bottom-4 w-0.5 bg-slate-200 hidden sm:block" />
            {STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <li key={s.num} className="relative">
                  <div className="flex gap-5">
                    <div className="relative flex-shrink-0">
                      <div className="w-16 h-16 rounded-2xl bg-[#0B1C3F] flex items-center justify-center ring-8 ring-white">
                        <Icon className="w-7 h-7 text-[#C9A84C]" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 pt-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-[13px] font-extrabold tracking-[0.15em] uppercase text-[#C9A84C]">
                          Step {s.num} · {s.kicker}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                          <Clock className="w-3 h-3" /> {s.time}
                        </span>
                      </div>
                      <h2 className="mt-1 text-[22px] sm:text-[26px] font-extrabold tracking-tight text-slate-900">
                        {s.title}
                      </h2>
                      <div className="mt-3 space-y-3">
                        {s.body.map((p, i) => (
                          <p
                            key={i}
                            className="text-[15px] sm:text-[16px] leading-[1.6] text-slate-600"
                          >
                            {p}
                          </p>
                        ))}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* TIMELINE COMPARISON */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-center text-[28px] sm:text-[38px] font-extrabold tracking-tight text-slate-900">
            The Old Way <span className="text-slate-400">vs.</span>{" "}
            <span className="text-[#C9A84C]">The TailorWiz Way</span>
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* OLD WAY */}
            <div className="rounded-2xl border-2 border-rose-200 bg-white p-6">
              <h3 className="text-[17px] font-extrabold text-rose-600 uppercase tracking-wide">
                The Old Way
              </h3>
              <ul className="mt-4 space-y-2.5">
                {[
                  "Generic resume sent to 200 jobs",
                  "2–5% response rate",
                  "4 to 6 weeks per application cycle",
                  "4.7 month average search",
                  "Tens of thousands in lost income",
                  "Months of family stress",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-[14px] text-slate-600">
                    <X className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* TAILORWIZ WAY */}
            <div className="rounded-2xl border-2 border-[#C9A84C] bg-[#0B1C3F] p-6">
              <h3 className="text-[17px] font-extrabold text-[#C9A84C] uppercase tracking-wide">
                The TailorWiz Way
              </h3>
              <ul className="mt-4 space-y-2.5">
                {[
                  "Qualified jobs only",
                  "90%+ match documents",
                  "Direct delivery to the decision maker",
                  "35–45% response rate",
                  "Application to interview in 72 hours",
                  "30-day search timelines",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-[14px] text-slate-100">
                    <Check className="w-4 h-4 text-[#C9A84C] flex-shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 sm:py-24 bg-gradient-to-b from-white to-slate-50 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-[30px] sm:text-[42px] font-extrabold tracking-tight text-slate-900 leading-[1.1]">
            Run Your First Job Through TailorWiz{" "}
            <span className="text-[#C9A84C]">Free.</span>
          </h2>
          <p className="mt-5 text-[16px] sm:text-[18px] text-slate-600 leading-[1.6]">
            Qualify, test, tailor, review, and deliver — see the whole workflow
            in action with a membership — activated the same day you subscribe.
          </p>
          <div className="mt-8">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#C9A84C] text-[#0B1C3F] text-[16px] font-bold shadow-lg shadow-[#C9A84C]/30 hover:bg-[#E5C36A] transition-all"
            >
              Try It Out FREE Now <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
